import React, { useState, useEffect, useRef } from 'react';
// Import the JSON-based configuration
import appConfig from '../config/appConfig';

const CreateUserDialog = ({
  isOpen,
  onClose,
  onSubmit,
  selectedAccountId,
  isEditMode,
  userToEdit,
  permissions,
  loginToken,
  corporateAccount,
  currentProject
}) => {
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessLevel, setAccessLevel] = useState('');
  const [defaultProject, setDefaultProject] = useState('');
  const [userStatus, setUserStatus] = useState('');
  const [businessTeamId, setBusinessTeamId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [error, setError] = useState('');
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [businessTeams, setBusinessTeams] = useState([]);
  const [loadingBusinessTeams, setLoadingBusinessTeams] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUserRoles, setLoadingUserRoles] = useState(false);

  const userIdInputRef = useRef(null);
const passwordRef = useRef(null);
const confirmPasswordRef = useRef(null);

  // Updated dialog styles with fixed layout
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: '500px',
      maxWidth: '90vw',
      height: 'auto',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Fetch projects when dialog opens and account is selected
  const fetchProjects = async (accountId) => {
    if (!accountId || !loginToken) return;

    setLoadingProjects(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_project_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: accountId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setProjects(data.project_list || []);
      } else {
        console.error('Failed to fetch projects:', data.status_description);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch statuses for the status dropdown
  const fetchStatuses = async () => {
    if (!corporateAccount || !loginToken) return;

    setLoadingStatuses(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId,
          project_id: "ALL PROJECTS",
          entity: "USER_STATUS"
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setStatuses(data.entity_statuses || []);
      } else {
        console.error('Failed to fetch statuses:', data.status_description);
        setStatuses([]);
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
      setStatuses([]);
    } finally {
      setLoadingStatuses(false);
    }
  };
// Function to clear password fields and prevent autofill
const clearPasswordFields = () => {
  setPassword('');
  setConfirmPassword('');

  // Clear the actual input values as well
  setTimeout(() => {
    if (passwordRef.current) {
      passwordRef.current.value = '';
    }
    if (confirmPasswordRef.current) {
      confirmPasswordRef.current.value = '';
    }
  }, 100);
};
  // Fetch business teams for the selected project
  const fetchBusinessTeams = async (projectId) => {
    if (!projectId || !corporateAccount || !loginToken) return;

    setLoadingBusinessTeams(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_business_team_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId,
          project_id: projectId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setBusinessTeams(data.business_team_list || []);
      } else {
        console.error('Failed to fetch business teams:', data.status_description);
        setBusinessTeams([]);
      }
    } catch (error) {
      console.error('Error fetching business teams:', error);
      setBusinessTeams([]);
    } finally {
      setLoadingBusinessTeams(false);
    }
  };

  // Fetch user roles
  const fetchUserRoles = async (projectId = null) => {
    if (!corporateAccount || !loginToken) return;

    setLoadingUserRoles(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({
          corporate_account: selectedAccountId,
          project_id: projectId,
          entity: "USER ROLES"
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setUserRoles(data.entity_statuses || []);
      } else {
        console.error('Failed to fetch user roles:', data.status_description);
        setUserRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
    } finally {
      setLoadingUserRoles(false);
    }
  };

  // Handle default project change
  const handleDefaultProjectChange = (projectId) => {
    setDefaultProject(projectId);

    // Reset business team and user role when project changes
    setBusinessTeamId('');
    setUserRole('');

    // Fetch business teams and user roles for the selected project
    if (projectId) {
      fetchBusinessTeams(projectId);
      fetchUserRoles(projectId);
    }

  };

  // Focus the user ID field whenever the dialog opens or after successful creation
  useEffect(() => {
    if (isOpen && userIdInputRef.current) {
      setTimeout(() => {
        userIdInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  // Reset form and focus when dialog opens, and fetch projects and statuses
  useEffect(() => {
    if (isOpen) {
      setShowValidationErrors(false);
      setError('');

      // Always clear password fields for security (both create and edit modes)
   clearPasswordFields();


      // Fetch projects for the selected account
      if (selectedAccountId) {
        fetchProjects(selectedAccountId);
      }

      // Fetch statuses for the status dropdown (only for edit mode)
      if (isEditMode) {
        fetchStatuses();
      }

      // Fetch user roles initially
//      fetchUserRoles();

      if (isEditMode && userToEdit) {
        setUserId(userToEdit.user_id);
        setUserName(userToEdit.user_name);
        setAccessLevel(userToEdit.access_level ? userToEdit.access_level.toString() : '');
        setDefaultProject(userToEdit.default_project || '');
        setUserStatus(userToEdit.status || '');
        setBusinessTeamId(userToEdit.business_team_id || '');
        setUserRole(userToEdit.user_role || '');

        // If there's a default project, fetch business teams for it
        if (userToEdit.default_project) {
          fetchBusinessTeams(userToEdit.default_project);
          fetchUserRoles(userToEdit.default_project);
        }
      } else {
        // Reset form for new user creation
        setUserId('');
        setUserName('');
        setAccessLevel('');
        setDefaultProject('');
        setUserStatus('');
        setBusinessTeamId('');
        setUserRole('');
      }
    }

setTimeout(() => {
  clearPasswordFields();
}, 500);


  }, [isOpen, isEditMode, userToEdit, selectedAccountId, corporateAccount, currentProject]);

  const resetFormAfterCreate = () => {
    setUserId('');
    setUserName('');
clearPasswordFields();
    setAccessLevel('');
    setDefaultProject('');
    setUserStatus('');
    setBusinessTeamId('');
    setUserRole('');
    setShowValidationErrors(false);
    setError('');

    // Reset business teams when form is reset
    setBusinessTeams([]);

    // Focus the user ID field again for next user creation
    setTimeout(() => {
      if (userIdInputRef.current) {
        userIdInputRef.current.focus();
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowValidationErrors(true);
    setError('');
    setIsLoading(true);

    if (!selectedAccountId) {
      setError('Please select an account before creating a user');
      setIsLoading(false);
      return { success: false };
    }

    // Validation checks
    if (!userId.trim()) {
      setError('User ID is required');
      setIsLoading(false);
      return { success: false };
    }

    if (!userName.trim()) {
      setError('User Name is required');
      setIsLoading(false);
      return { success: false };
    }

    // Password validation for new users (always required)
    if (!isEditMode && !password.trim()) {
      setError('Password is required');
      setIsLoading(false);
      return { success: false };
    }

    // Password confirmation validation
    if (password.trim()) {
      if (!confirmPassword.trim()) {
        setError('Please confirm your password');
        setIsLoading(false);
        return { success: false };
      }

      if (password !== confirmPassword) {
        setError('Password and confirm password do not match');
        setIsLoading(false);
        return { success: false };
      }
    }

    if (!accessLevel) {
      setError('Access Level is required');
      setIsLoading(false);
      return { success: false };
    }

    // Status validation for edit mode
    if (isEditMode && !userStatus) {
      setError('User Status is required');
      setIsLoading(false);
      return { success: false };
    }

    const endpoint = isEditMode ?
      appConfig.getUrl('/update_user') :
      appConfig.getUrl('/create_user');

    // Prepare request body
    const requestBody = {
      corporate_account: corporateAccount,
      requesting_for_corporate_account: selectedAccountId,
      user_id: userId,
      user_name: userName,
      access_level: parseInt(accessLevel),
      default_project: defaultProject
    };

    // Only include password if we're creating a new user or if password is provided for edit
    if (!isEditMode || password.trim()) {
      requestBody.password = password;
    }

    // Include user_status for edit mode
    if (isEditMode) {
      requestBody.user_status = userStatus;
    }

    // Include optional business team and user role if selected
    if (businessTeamId) {
      requestBody.business_team_id = businessTeamId;
    }

    if (userRole) {
      requestBody.user_role = userRole;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setError(`User ${isEditMode ? 'updated' : 'created'} successfully`);

        // Call onSubmit to refresh the user list
        onSubmit();

        // Handle post-success behavior
        setTimeout(() => {
          if (isEditMode) {
            onClose();
          } else {
            resetFormAfterCreate();
          }
        }, 1500);

        setIsLoading(false);
        return {
          success: true,
          isEditMode: isEditMode,
          userId: data.user_id
        };
      } else {
        throw new Error(data.status_description || 'Failed to save user');
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      return { success: false };
    }
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* Fixed Header */}
        <div style={dialogStyles.header}>
          {isEditMode ? `Edit User (${userToEdit?.user_id})` : 'Create New User'}
        </div>

        {/* Scrollable Content */}
        <div style={dialogStyles.content}>
<form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }} autoComplete="off">

{/* Hidden dummy fields to confuse autofill */}
<div style={{ display: 'none' }}>
  <input type="text" name="fake_username" autoComplete="username" />
  <input type="password" name="fake_password" autoComplete="new-password" />
</div>


            <div style={{ flex: 1 }}>
              {/* User ID */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  User ID (Email): <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  ref={userIdInputRef}
                  type="email"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${showValidationErrors && !userId.trim() ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="user@example.com"
                  disabled={isEditMode}
                    autoComplete="off"
                  required
                />
                {showValidationErrors && !userId.trim() && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    User ID is required
                  </span>
                )}
              </div>

              {/* User Name */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  User Name: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${showValidationErrors && !userName.trim() ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="Full Name"
                    autoComplete="off"
                  required
                />
                {showValidationErrors && !userName.trim() && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    User Name is required
                  </span>
                )}
              </div>

              {/* Password fields for creating new user */}
              {!isEditMode && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Password: <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      ref={passwordRef}
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${showValidationErrors && !password.trim() ? '#dc2626' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter password"
                      required
                    />
                    {showValidationErrors && !password.trim() && (
                      <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Password is required
                      </span>
                    )}
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Confirm Password: <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <input
                      ref={confirmPasswordRef}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${showValidationErrors && (password !== confirmPassword || (!confirmPassword.trim() && password.trim())) ? '#dc2626' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="Confirm password"
                      required
                    />
                    {showValidationErrors && password.trim() && !confirmPassword.trim() && (
                      <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Please confirm your password
                      </span>
                    )}
                    {showValidationErrors && password.trim() && confirmPassword.trim() && password !== confirmPassword && (
                      <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Passwords do not match
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Password fields for editing - optional */}
              {isEditMode && (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      New Password: <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280' }}>(leave blank to keep current password)</span>
                    </label>
                    <input
                      ref={passwordRef}
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="Enter new password (optional)"
                    />
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Confirm New Password: {password.trim() && <span style={{ color: '#dc2626' }}>*</span>}
                    </label>
                    <input
                      ref={confirmPasswordRef}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${showValidationErrors && password.trim() && password !== confirmPassword ? '#dc2626' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                      placeholder="Confirm new password"
                      disabled={!password.trim()}
                    />
                    {showValidationErrors && password.trim() && !confirmPassword.trim() && (
                      <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Please confirm your new password
                      </span>
                    )}
                    {showValidationErrors && password.trim() && confirmPassword.trim() && password !== confirmPassword && (
                      <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Passwords do not match
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Access Level */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Access Level: <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <span style={{ display: 'block', textAlign: 'center', color: 'red',  margin: '10px 0' }}>
                  This is the default access level that takes effect if no project specific access level is defined
                </span>
                <select
                  value={accessLevel}
                  onChange={(e) => setAccessLevel(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${showValidationErrors && !accessLevel ? '#dc2626' : '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                  required
                >
                  <option value="">Select access level</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                    <option key={level} value={level.toString()}>
                      {level}
                    </option>
                  ))}
                </select>
                {showValidationErrors && !accessLevel && (
                  <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Access Level is required
                  </span>
                )}
              </div>

              {/* User Status - Only show for edit mode */}
              {isEditMode && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    User Status: <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  {loadingStatuses ? (
                    <div style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <svg style={{
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px',
                        width: '16px',
                        height: '16px'
                      }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading statuses...
                    </div>
                  ) : (
                    <select
                      value={userStatus}
                      onChange={(e) => setUserStatus(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: `1px solid ${showValidationErrors && !userStatus ? '#dc2626' : '#ddd'}`,
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      required
                    >
                      <option value="">Select a status</option>
                      {statuses.map(status => (
                        <option key={status.status} value={status.status}>
                          {status.status}
                        </option>
                      ))}
                    </select>
                  )}
                  {showValidationErrors && !userStatus && (
                    <span style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      User Status is required
                    </span>
                  )}
                  {statuses.length === 0 && !loadingStatuses && (
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginTop: '4px'
                    }}>
                      No statuses available
                    </div>
                  )}
                </div>
              )}

              {/* Default Project */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Default Project:
                </label>
                {loadingProjects ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <svg style={{
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                      width: '16px',
                      height: '16px'
                    }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading projects...
                  </div>
                ) : (
                  <select
                    value={defaultProject}
                    onChange={(e) => handleDefaultProjectChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select a project (optional)</option>
                    {projects.map(project => (
                      <option key={project.project_id} value={project.project_id}>
                        {project.project_description} ({project.project_id})
                      </option>
                    ))}
                  </select>
                )}
                {projects.length === 0 && !loadingProjects && selectedAccountId && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    No projects available for this account
                  </div>
                )}
              </div>

              {/* Business Team */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Project Team: <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280' }}>(Optional)</span>
                </label>
                {loadingBusinessTeams ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <svg style={{
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                      width: '16px',
                      height: '16px'
                    }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading business teams...
                  </div>
                ) : (
                  <select
                    value={businessTeamId}
                    onChange={(e) => setBusinessTeamId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    disabled={!defaultProject}
                  >
                    <option value="">Select a project team (optional)</option>
                    {businessTeams
                      .filter(team => team.business_team_status === 'Active')
                      .map(team => (
                        <option key={team.business_team_id} value={team.business_team_id}>
                          {team.business_team_description}
                        </option>
                      ))
                    }
                  </select>
                )}
                {!defaultProject && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    Please select a default project first to load business teams
                  </div>
                )}
                {businessTeams.length === 0 && !loadingBusinessTeams && defaultProject && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    No business teams available for this project
                  </div>
                )}
              </div>

              {/* User Role */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  User Role: <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6b7280' }}>(Optional)</span>
                </label>
                {loadingUserRoles ? (
                  <div style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <svg style={{
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px',
                      width: '16px',
                      height: '16px'
                    }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading user roles...
                  </div>
                ) : (
                  <select
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select a user role (optional)</option>
                    {userRoles.map(role => (
                      <option key={role.status} value={role.status}>
                        {role.status}
                      </option>
                    ))}
                  </select>
                )}
                {userRoles.length === 0 && !loadingUserRoles && (
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    No user roles available
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Footer */}
        <div style={dialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              disabled={isLoading}
            >
              Close
            </button>

            <button
              type="submit"
              onClick={(e) => {
                if (!permissions.canUpdate) {
                  e.preventDefault();
                  setError('You do not have permission to create or edit users');
                  return;
                }
                handleSubmit(e);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: !permissions.canUpdate ? '#f3f4f6' : '#C8F4DE',
                color: !permissions.canUpdate ? '#6b7280' : 'black',
                border: !permissions.canUpdate ? '1px solid #d1d5db' : 'none',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: isLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center'
              }}
              disabled={isLoading}
            >
              {isLoading && (
                <svg style={{
                  animation: 'spin 1s linear infinite',
                  marginLeft: '-4px',
                  marginRight: '8px',
                  width: '16px',
                  height: '16px'
                }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {!permissions.canUpdate ?
                (isEditMode ? 'No Permission to Update' : 'No Permission to Create') :
                (isLoading ? 'Processing...' : (isEditMode ? 'Update User' : 'Create User'))
              }
            </button>
          </div>
        </div>
      </div>

      {/* Add CSS for spin animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CreateUserDialog;